import React, { useRef } from 'react';
import { FormikValues } from 'formik';

import { Card, CardContent, TextField } from '@material-ui/core';
import AssessmentIcon from '@material-ui/icons/Assessment';
import { Autocomplete } from '@material-ui/lab';
import { FetchError } from '../api/api';
import CardTitle from '../card/CardTitle';
import InlineFormik from '../inline/InlineFormik';
import InlineForm from '../inline/InlineForm';
import InlineTextField from '../inline/InlineTextField';
import InlineHtml from '../inline/InlineHtml';
import { InlineMultiSelect } from '../inline/InlineMultiSelect';
import InlineSelect from '../inline/InlineSelect';
import { InlineMultiSelectOption } from '../inline/InlineMultiSelect';
import { InlineSelectOptions } from '../inline/InlineSelect';
import { InlineFileValue } from '../inline/InlineFile';
import { InlineFormControl } from '../inline/InlineFormControl';
import { RiskCategoryOptions } from '../../secure-portal/risk-management/cards/RiskAssessmentCard';
import { Task } from '../../secure-portal/tasks/Task.interface';
import { useTaskEntityFields } from '../../secure-portal/workflow/Workflow.hooks';
import environment from '../../environments';

const RiskAssessmentOption: InlineSelectOptions[] = [
  { value: 1, label: '1' },
  { value: 2, label: '2' },
  { value: 3, label: '3' },
  { value: 4, label: '4' },
  { value: 5, label: '5' }
];

const RiskRelevanceOptions: InlineMultiSelectOption[] = [
  { value: 'patientSafety', label: 'Patient Safety' },
  { value: 'compliance', label: 'Compliance' },
  { value: 'business', label: 'Business' }
];

export function getAssessmentInitialValues(data?: any) {
  return {
    relatedHazard: data?.relatedHazard || '',
    hazardSituation: data?.hazardSituation || '',
    potentialHarm: data?.potentialHarm || '',
    potentialCauses: data?.potentialCauses || '',
    relevance: data?.relevance || [],
    category: data?.category || [],
    currentControls: data?.currentControls,
    recommendedActions: data?.recommendedActions || '',
    actionsTaken: data?.actionsTaken || '',
    verification: data?.verification || '',
    p1: data?.p1 || 0,
    s1: data?.s1 || 0,
    d1: data?.d1 || 0,
    p2: data?.p2 || 0,
    s2: data?.s2 || 0,
    d2: data?.d2 || 0,
    rpn1: data?.rpn1 || 0,
    r1: data.r1,
    rpn2: data?.rpn2 || 0,
    r2: data.r2
  };
}

export interface AssessmentCardProps {
  task: Task;
  showCategory?: boolean;
  isLoading: boolean;
  error?: FetchError;
  initialValues?: Partial<any>;
  useCustomFields?: boolean;
  onSubmit: (values: Partial<any>) => void;
  onChangeAttachments: (attachments: InlineFileValue[]) => void;
}

const AssessmentCard: React.FC<AssessmentCardProps> = ({
  task,
  showCategory,
  isLoading,
  error,
  initialValues,
  useCustomFields,
  onSubmit,
  onChangeAttachments
}) => {
  const formikRef = useRef<FormikValues>();
  const { isFieldDisabled } = useTaskEntityFields(task);
  const riskAssessmentOption = useCustomFields
    ? RiskAssessmentOption
    : RiskAssessmentOption.slice(0, 3);
  return (
    <Card>
      <CardTitle>
        <AssessmentIcon />
        Risk Assessment
      </CardTitle>
      <CardContent>
        <InlineFormik
          innerRef={(formik: any) => (formikRef.current = formik)}
          isSubmitting={isLoading}
          error={error}
          initialValues={initialValues}
          onSubmit={onSubmit}
        >
          {({ values, setFieldValue, submitForm }) => (
            <InlineForm>
              <InlineTextField
                name='relatedHazard'
                label='Related Hazard'
                disabled={isFieldDisabled('relatedHazard')}
              />
              <InlineTextField
                name='hazardSituation'
                label='Potential Failure Mode (Hazardous Situation)'
                disabled={isFieldDisabled('hazardSituation')}
              />
              <InlineTextField
                name='potentialHarm'
                label='Potential Failure Effects (Potential Harm)'
                disabled={isFieldDisabled('potentialHarm')}
              />
              <InlineHtml
                name='potentialCauses'
                label='Potential Causes'
                attachments={task.attachments || []}
                onChangeAttachments={onChangeAttachments}
                disabled={isFieldDisabled('potentialCauses')}
              />
              {showCategory && !environment.riskConfig?.hideCategory ? (
                <InlineFormControl label='Risk Category'>
                  <Autocomplete
                    multiple
                    fullWidth
                    filterSelectedOptions
                    disabled={isFieldDisabled('category')}
                    value={values.category || undefined}
                    options={RiskCategoryOptions}
                    onChange={(_, category) => {
                      setFieldValue('category', category || undefined, true);
                      submitForm();
                    }}
                    renderInput={params => (
                      <TextField {...params} fullWidth variant='outlined' />
                    )}
                  />
                </InlineFormControl>
              ) : (
                <InlineMultiSelect
                  name='relevance'
                  label='Relevance'
                  options={RiskRelevanceOptions}
                  disabled={isFieldDisabled('relevance')}
                />
              )}
              <InlineSelect
                name='p1'
                label={useCustomFields ? '01' : 'P1'}
                grid={{ xs: 3 }}
                options={riskAssessmentOption}
                disabled={isFieldDisabled('p1')}
              />
              <InlineSelect
                name='s1'
                label='S1'
                grid={{ xs: 3 }}
                options={riskAssessmentOption}
                disabled={isFieldDisabled('s1')}
              />
              {!useCustomFields && (
                <InlineSelect
                  name='d1'
                  label='D1'
                  grid={{ xs: 3 }}
                  options={riskAssessmentOption}
                  disabled={isFieldDisabled('d1')}
                />
              )}
              <InlineTextField
                disabled
                name='rpn1'
                label='RPN1'
                grid={{ xs: 3 }}
              />
              {useCustomFields && (
                <InlineTextField
                  disabled
                  name='r1'
                  label='R1'
                  grid={{ xs: 3 }}
                />
              )}
              <InlineTextField
                name='currentControls'
                label='Current Controls'
                disabled={isFieldDisabled('currentControls')}
              />
              <InlineHtml
                name='recommendedActions'
                label='Recommended Actions/Mitigations'
                attachments={task.attachments || []}
                onChangeAttachments={onChangeAttachments}
                disabled={isFieldDisabled('recommendedActions')}
              />
              <InlineHtml
                name='actionsTaken'
                label='Actions Taken'
                attachments={task.attachments || []}
                onChangeAttachments={onChangeAttachments}
                disabled={isFieldDisabled('actionsTaken')}
              />
              <InlineHtml
                name='verification'
                label='Verification'
                attachments={task.attachments || []}
                onChangeAttachments={onChangeAttachments}
                disabled={isFieldDisabled('verification')}
              />
              <InlineSelect
                name='p2'
                label={useCustomFields ? '02' : 'P2'}
                grid={{ xs: 3 }}
                options={riskAssessmentOption}
                disabled={isFieldDisabled('p2')}
              />
              <InlineSelect
                name='s2'
                label='S2'
                grid={{ xs: 3 }}
                options={riskAssessmentOption}
                disabled={isFieldDisabled('s2')}
              />
              {!useCustomFields && (
                <InlineSelect
                  name='d2'
                  label='D2'
                  grid={{ xs: 3 }}
                  options={riskAssessmentOption}
                  disabled={isFieldDisabled('d2')}
                />
              )}
              <InlineTextField
                disabled
                name='rpn2'
                label='RPN2'
                grid={{ xs: 3 }}
              />
              {useCustomFields && (
                <InlineTextField
                  disabled
                  name='r2'
                  label='R2'
                  grid={{ xs: 3 }}
                />
              )}
            </InlineForm>
          )}
        </InlineFormik>
      </CardContent>
    </Card>
  );
};

export default AssessmentCard;
